import { useState, useMemo, useEffect, useCallback } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { DUMMY_LEADS_DATA } from "utils/dummyData";
import {
  getFilteredLeads,
  getUniqueAgents,
  DATE_RANGES,
} from "utils/reportFilters";
import {
  fetchLeadAllocationReport,
  fetchLeadSourceReport,
  fetchLeadStatusReport,
  fetchReportLeadSourcesFilter,
} from "services/reports/totalLeadsReport.service";
import { getInternalUsersForAssigningLeads } from "services/userService";
import { getStageAndSubStages } from "services/leadStageService";
import { getLeadsSources } from "services/leadsourcesServices";
import { SalesPageLayout } from "app/layouts/SalesLayout/SalesPageLayout";
import { ReportsFilterDrawer } from "components/reports/ReportsFilterDrawer";
import { Button } from "components/ui";
import { FunnelIcon } from "@heroicons/react/24/outline";

const INITIAL_FILTERS = {
  quickRange: "all",
  agent: "",
  status: [], // stageIds array
  subStatus: [], // subStageIds array
  fromDate: "",
  toDate: "",
  rowVar: "agent",
  colVar: "status",
  leadSource: "",
};

// Converts the quick range picker to fromDate/toDate strings for the API
function buildDatePayload(filters) {
  const now = new Date();
  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const r = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${r}`;
  };

  if (filters.quickRange === DATE_RANGES.THIS_MONTH) {
    return {
      fromDate: fmt(new Date(now.getFullYear(), now.getMonth(), 1)),
      toDate: null,
    };
  }
  if (filters.quickRange === DATE_RANGES.LAST_MONTH) {
    return {
      fromDate: fmt(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
      toDate: fmt(new Date(now.getFullYear(), now.getMonth(), 0)),
    };
  }
  if (filters.quickRange === DATE_RANGES.LAST_3_MONTHS) {
    return {
      fromDate: fmt(new Date(now.getFullYear(), now.getMonth() - 3, 1)),
      toDate: null,
    };
  }
  if (filters.quickRange === DATE_RANGES.THIS_YEAR) {
    return { fromDate: fmt(new Date(now.getFullYear(), 0, 1)), toDate: null };
  }
  if (filters.quickRange === DATE_RANGES.CUSTOM) {
    return {
      fromDate: filters.fromDate || null,
      toDate: filters.toDate || null,
    };
  }
  return { fromDate: null, toDate: null };
}

export default function ReportsLayout() {
  const location = useLocation();

  const isAllocationRoute = location.pathname.includes("lead-allocation");
  const isSourceRoute = location.pathname.includes("lead-source");
  const isStatusRoute = location.pathname.includes("lead-status");

  // Separate module-wise filter states so each report maintains its own independent filters
  const [allocationFilters, setAllocationFilters] = useState(INITIAL_FILTERS);
  const [sourceFilters, setSourceFilters] = useState(INITIAL_FILTERS);
  const [statusFilters, setStatusFilters] = useState(INITIAL_FILTERS);

  const activeFilters = useMemo(() => {
    if (isAllocationRoute) return allocationFilters;
    if (isSourceRoute) return sourceFilters;
    if (isStatusRoute) return statusFilters;
    return INITIAL_FILTERS;
  }, [isAllocationRoute, isSourceRoute, isStatusRoute, allocationFilters, sourceFilters, statusFilters]);

  const setActiveFilters = useCallback(
    (newFilters) => {
      if (isAllocationRoute) {
        setAllocationFilters(newFilters);
      } else if (isSourceRoute) {
        setSourceFilters(newFilters);
      } else if (isStatusRoute) {
        setStatusFilters(newFilters);
      }
    },
    [isAllocationRoute, isSourceRoute, isStatusRoute],
  );

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // ── Active System Agents & Stages ─────────────────────────────────────────
  const [realAgents, setRealAgents] = useState([]);
  const [realStages, setRealStages] = useState([]);
  const [realSources, setRealSources] = useState([]);

  useEffect(() => {
    getInternalUsersForAssigningLeads().then((res) => {
      if (res.success && res.data) {
        setRealAgents(res.data);
      }
    });
    getStageAndSubStages({ pageSize: 100 }).then((res) => {
      if (Array.isArray(res)) {
        setRealStages(res);
      }
    });
    if (isSourceRoute) {
      fetchReportLeadSourcesFilter().then((res) => {
        if (res.data?.data) {
          setRealSources(res.data.data);
        }
      });
    } else {
      getLeadsSources().then((res) => {
        if (res.success && res.data) {
          setRealSources(res.data);
        }
      });
    }
  }, [isSourceRoute]);

  // ── Allocation Report state ──────────────────────────────────────────────
  const [allocationReport, setAllocationReport] = useState({
    columns: [],
    rows: [],
  });
  const [allocationLoading, setAllocationLoading] = useState(false);
  // Cache: { [`${agentId}_${pageNumber}`]: { data: [], totalRecords: 0 } }
  const [drillDownCache, setDrillDownCache] = useState({});

  const loadAllocationReport = useCallback(
    async (
      currentFilters,
      search = null,
      sortColumn = "AgentName",
      sortDirection = "ASC",
    ) => {
      setAllocationLoading(true);
      setDrillDownCache({}); // clear drill-down cache whenever summary is refreshed
      try {
        const { fromDate, toDate } = buildDatePayload(currentFilters);
        const res = await fetchLeadAllocationReport({
          fromDate,
          toDate,
          filterAgentId: (currentFilters.agent && currentFilters.agent !== "all") ? currentFilters.agent : null,
          search: search || null,
          sortColumn,
          sortDirection,
        });
        if (res.data?.data) {
          setAllocationReport(res.data.data); // { columns, rows }
        }
      } catch (err) {
        console.error("Allocation report error:", err);
      } finally {
        setAllocationLoading(false);
      }
    },
    [],
  );

  const fetchAgentDrillDown = useCallback(
    async (agentId, sortColumn = "Date", sortDirection = "DESC") => {
      const cacheKey = `${agentId}_${sortColumn}_${sortDirection}`;
      if (drillDownCache[cacheKey]) return drillDownCache[cacheKey];

      const { fromDate, toDate } = buildDatePayload(allocationFilters);
      const res = await fetchLeadAllocationReport({
        fromDate,
        toDate,
        agentId,
        sortColumn,
        sortDirection,
      });

      const result = {
        data: JSON.parse(res.data?.data || "[]"),
        totalRecords: res.data?.totalRecords || 0,
      };
      setDrillDownCache((prev) => ({ ...prev, [cacheKey]: result }));
      return result;
    },
    [drillDownCache, allocationFilters],
  );

  // Load allocation data on mount and when allocation filters change
  useEffect(() => {
    if (isAllocationRoute) {
      loadAllocationReport(allocationFilters);
    }
  }, [isAllocationRoute, allocationFilters, loadAllocationReport]);

  // ── Lead Source Report state ─────────────────────────────────────────────
  const [leadSourceReport, setLeadSourceReport] = useState({
    columns: [],
    rows: [],
    totalRecords: 0,
    page: 1,
    limit: 100,
  });
  const [leadSourceLoading, setLeadSourceLoading] = useState(false);
  // Cache: { [`${leadSourceId}_${leadSourceFormId}_${pageNumber}`]: { data: [], totalRecords: 0 } }
  const [sourceDrillDownCache, setSourceDrillDownCache] = useState({});

  const loadLeadSourceReport = useCallback(
    async (
      currentFilters,
      page = 1,
      pageSize = 100,
      search = null,
      sortColumn = "SourceChannel",
      sortDirection = "ASC",
    ) => {
      setLeadSourceLoading(true);
      setSourceDrillDownCache({}); // clear cache whenever summary is refreshed
      try {
        const { fromDate, toDate } = buildDatePayload(currentFilters);
        const stageIds = Array.isArray(currentFilters.status)
          ? currentFilters.status
          : currentFilters.status
            ? [currentFilters.status]
            : [];
        const subStageIds = Array.isArray(currentFilters.subStatus)
          ? currentFilters.subStatus
          : currentFilters.subStatus
            ? [currentFilters.subStatus]
            : [];

        const res = await fetchLeadSourceReport({
          fromDate,
          toDate,
          agentId: (currentFilters.agent && currentFilters.agent !== "all") ? currentFilters.agent : null,
          filterLeadSourceId: currentFilters.leadSource || null,
          stageIds,
          subStageIds,
          pageNumber: page,
          pageSize: pageSize,
          search: search || null,
          sortColumn,
          sortDirection,
        });
        if (res.data?.data) {
          setLeadSourceReport({
            columns: res.data.data.columns || [],
            rows: res.data.data.rows || [],
            totalRecords: res.data.data.totalRecords || 0,
            page,
            limit: pageSize,
          });
        }
      } catch (err) {
        console.error("Lead source report error:", err);
        setLeadSourceReport({
          columns: [],
          rows: [],
          totalRecords: 0,
          page,
          limit: pageSize,
        });
      } finally {
        setLeadSourceLoading(false);
      }
    },
    [],
  );

  const fetchSourceDrillDown = useCallback(
    async (
      leadSourceId,
      leadSourceFormId = null,
      sortColumn = "Date",
      sortDirection = "DESC",
    ) => {
      const cacheKey = `${leadSourceId}_${leadSourceFormId || "null"}_${sortColumn}_${sortDirection}`;
      if (sourceDrillDownCache[cacheKey]) return sourceDrillDownCache[cacheKey];

      const { fromDate, toDate } = buildDatePayload(sourceFilters);
      const stageIds = Array.isArray(sourceFilters.status)
        ? sourceFilters.status
        : sourceFilters.status
          ? [sourceFilters.status]
          : [];
      const subStageIds = Array.isArray(sourceFilters.subStatus)
        ? sourceFilters.subStatus
        : sourceFilters.subStatus
          ? [sourceFilters.subStatus]
          : [];

      const res = await fetchLeadSourceReport({
        fromDate,
        toDate,
        agentId: (sourceFilters.agent && sourceFilters.agent !== "all") ? sourceFilters.agent : null,
        leadSourceId,
        leadSourceFormId,
        stageIds,
        subStageIds,
        sortColumn,
        sortDirection,
      });

      const result = {
        data: JSON.parse(res.data?.data || "[]"),
        totalRecords: res.data?.totalRecords || 0,
      };
      setSourceDrillDownCache((prev) => ({ ...prev, [cacheKey]: result }));
      return result;
    },
    [sourceDrillDownCache, sourceFilters],
  );

  // Load source data on mount and when source filters change
  useEffect(() => {
    if (isSourceRoute) {
      loadLeadSourceReport(sourceFilters);
    }
  }, [isSourceRoute, sourceFilters, loadLeadSourceReport]);

  // ── Lead Status Report state ─────────────────────────────────────────────
  const [leadStatusReport, setLeadStatusReport] = useState({
    data: [],
    totalRecords: 0,
    page: 1,
    limit: 100,
  });
  const [leadStatusLoading, setLeadStatusLoading] = useState(false);

  const loadLeadStatusReport = useCallback(
    async (
      currentFilters,
      page = 1,
      pageSize = 100,
      search = null,
      sortColumn = "Date",
      sortDirection = "DESC",
    ) => {
      setLeadStatusLoading(true);
      try {
        const { fromDate, toDate } = buildDatePayload(currentFilters);
        const stageIds = Array.isArray(currentFilters.status)
          ? currentFilters.status
          : currentFilters.status
            ? [currentFilters.status]
            : [];
        const subStageIds = Array.isArray(currentFilters.subStatus)
          ? currentFilters.subStatus
          : currentFilters.subStatus
            ? [currentFilters.subStatus]
            : [];

        const res = await fetchLeadStatusReport({
          fromDate,
          toDate,
          agentId: (currentFilters.agent && currentFilters.agent !== "all") ? currentFilters.agent : null,
          stageIds,
          subStageIds,
          pageNumber: page,
          pageSize: pageSize,
          search: search || null,
          sortColumn,
          sortDirection,
        });
        if (res.data) {
          setLeadStatusReport({
            data: JSON.parse(res.data?.data || "[]"),
            totalRecords: res.data?.totalRecords || 0,
            page,
            limit: pageSize,
          });
        }
      } catch (err) {
        console.error("Lead status report error:", err);
        setLeadStatusReport({
          data: [],
          totalRecords: 0,
          page,
          limit: pageSize,
        });
      } finally {
        setLeadStatusLoading(false);
      }
    },
    [],
  );

  // Load status data on mount and when status filters change
  useEffect(() => {
    if (isStatusRoute) {
      loadLeadStatusReport(statusFilters);
    }
  }, [isStatusRoute, statusFilters, loadLeadStatusReport]);

  // ────────────────────────────────────────────────────────────────────────

  const agents = useMemo(() => getUniqueAgents(DUMMY_LEADS_DATA), []);
  const statuses = realStages;

  const stageOptions = useMemo(() => {
    return statuses.map((stage) => ({
      id: stage.stageId,
      value: stage.stageId,
      label: stage.stageName,
      type: "stage",
      children: (stage.subStages || stage.SubStages || []).map((subStage) => ({
        id: subStage.subStageId,
        value: subStage.subStageId,
        label: subStage.subStageName,
        type: "substage",
        parentId: stage.stageId,
      })),
    }));
  }, [statuses]);

  const pageMeta = useMemo(() => {
    const path = location.pathname;
    if (path.includes("lead-source"))
      return {
        title: "Lead Source Report",
        desc: "Lead generation by source channel — drill down to see individual leads",
        csv: "lead-source-report.csv",
        showAgent: true,
        showStatus: true,
        showSource: true,
        searchPlaceholder: "Search source channel...",
      };
    if (path.includes("lead-allocation"))
      return {
        title: "Lead Allocation Report",
        desc: "See how leads are distributed across users — click user to see their leads",
        csv: "lead-allocation-report.csv",
        showAgent: true,
        showStatus: false,
        searchPlaceholder: "Search users...",
      };
    if (path.includes("lead-status"))
      return {
        title: "Lead Status Report",
        desc: "Filter leads by their current status — funnel view",
        csv: "lead-status-report.csv",
        showAgent: true,
        showStatus: true,
        searchPlaceholder: "Search name, email, phone number...",
      };
    return {
      title: "Reports",
      desc: "Business intelligence and analytics",
      csv: "report.csv",
      searchPlaceholder: "Search...",
    };
  }, [location.pathname]);

  const applyFilters = useCallback(
    (filtersToApply = activeFilters) => {
      const data = getFilteredLeads(DUMMY_LEADS_DATA, filtersToApply);
      setIsLoading(true);
      setFilteredLeads(data);
      setIsLoading(false);
    },
    [activeFilters],
  );

  useEffect(() => {
    applyFilters(activeFilters);
  }, [activeFilters, applyFilters]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (activeFilters.quickRange && activeFilters.quickRange !== "all") count++;
    if (activeFilters.agent) count++;
    const hasStatus =
      (Array.isArray(activeFilters.status) && activeFilters.status.length > 0) ||
      (!Array.isArray(activeFilters.status) && activeFilters.status);
    const hasSubStatus =
      (Array.isArray(activeFilters.subStatus) &&
        activeFilters.subStatus.length > 0) ||
      (!Array.isArray(activeFilters.subStatus) && activeFilters.subStatus);
    if (hasStatus || hasSubStatus) count++;
    if (activeFilters.leadSource) count++;
    return count;
  }, [activeFilters]);

  return (
    <SalesPageLayout
      title={pageMeta.title}
      subtitle={pageMeta.desc}
      hideTabs={true}
      unifiedHeader={true}
      headerActions={
        <div className="flex items-center gap-2">
          <div className="relative">
            <Button
              variant="flat"
              isIcon
              className="dark:text-dark-300 dark:hover:text-dark-100 h-8 w-8 rounded-md text-gray-400 shadow-none hover:text-gray-600 md:h-9 md:w-9"
              title="Filter"
              onClick={() => setIsFilterDrawerOpen((prev) => !prev)}
            >
              <FunnelIcon className="h-5 w-5" />
            </Button>
            {activeFiltersCount > 0 && (
              <span className="bg-primary-500 dark:border-dark-700 absolute top-1 right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full border-2 border-white text-[9px] leading-none font-bold text-white shadow-none">
                {activeFiltersCount}
              </span>
            )}
          </div>
        </div>
      }
      customToolbar={
        isFilterDrawerOpen && (
          <div className="mt-2 mb-5">
            <ReportsFilterDrawer
              isOpen={isFilterDrawerOpen}
              onClose={() => setIsFilterDrawerOpen(false)}
              filters={activeFilters}
              onApply={(appliedFilters, shouldClose = true) => {
                setActiveFilters(appliedFilters);
                if (shouldClose) {
                  setIsFilterDrawerOpen(false);
                }
              }}
              onReset={() => {
                const cleared = { ...INITIAL_FILTERS };
                cleared.rowVar = activeFilters.rowVar;
                cleared.colVar = activeFilters.colVar;
                setActiveFilters(cleared);
                setIsFilterDrawerOpen(false);
              }}
              realAgents={realAgents}
              agents={agents}
              realSources={realSources}
              stageOptions={stageOptions}
              pageMeta={pageMeta}
            />
          </div>
        )
      }
    >
      <Outlet
        context={{
          filteredLeads,
          filters: activeFilters,
          setFilters: setActiveFilters,
          isLoading,
          applyFilters,
          agents,
          statuses,
          searchPlaceholder: pageMeta.searchPlaceholder,
          // Allocation report
          allocationReport,
          allocationLoading,
          fetchAgentDrillDown,
          loadAllocationReport,
          // Lead Source report
          leadSourceReport,
          leadSourceLoading,
          fetchSourceDrillDown,
          loadLeadSourceReport,
          // Lead Status report
          leadStatusReport,
          leadStatusLoading,
          loadLeadStatusReport,
        }}
      />
    </SalesPageLayout>
  );
}
